import { BarChart, BarChartProps } from '@mui/x-charts/BarChart';
import { MonthlyPaymentPattern, formatMoney } from '@dill/dill-shared';
import { COLORS } from '../../../../../utils/colors';

interface PaymentPatternsChartProps {
  data: MonthlyPaymentPattern[];
}

const getResponsiveConfig = (): Partial<BarChartProps> => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  return {
    height: isMobile ? 300 : 350,
    margin: {
      left: isMobile ? 50 : 60,
      right: isMobile ? 15 : 20,
      top: 20,
      bottom: isMobile ? 80 : 60,
    },
    hideLegend: false,
  };
};

function PaymentPatternsChart({ data }: PaymentPatternsChartProps) {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-400">
        No payment patterns data available
      </div>
    );
  }

  const timingDataSet = data.map((month) => ({
    month: month.month.split('-')[1],
    onTimePayments: month.onTimePayments,
    latePayments: month.latePayments,
  }));

  const amountDataSet = data.map((month) => ({
    month: month.month.split('-')[1],
    fullPayments: month.fullPayments,
    partialPayments: month.partialPayments,
  }));

  return (
    <div className="w-full space-y-8">
      {/* Payment Timing Chart */}
      <div>
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-6">
          <div>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1">
              Payment Timing Patterns
            </h3>
            <p className="text-sm text-gray-500">Monthly on-time vs late payments</p>
          </div>
        </div>
        <BarChart
          borderRadius={8}
          dataset={timingDataSet}
          xAxis={[
            {
              dataKey: 'month',
              tickLabelStyle: {
                fontSize: 12,
                fill: COLORS.TEXT_SECONDARY,
                fontFamily: 'Inter',
                fontWeight: 'bold',
              },
            },
          ]}
          series={[
            {
              dataKey: 'onTimePayments',
              label: 'On Time',
              color: COLORS.SUCCESS_500,
              valueFormatter: (value: number | null) => {
                return `${value?.toLocaleString() || 0} payments`;
              },
              stack: 'total',
            },
            {
              dataKey: 'latePayments',
              label: 'Late',
              color: COLORS.ERROR_500,
              valueFormatter: (value: number | null) => {
                return `${value?.toLocaleString() || 0} payments`;
              },
              stack: 'total',
            },
          ]}
          yAxis={[
            {
              width: 60,
              tickLabelStyle: {
                fontSize: 11,
                fill: COLORS.TEXT_SECONDARY,
                fontFamily: 'Inter',
                fontWeight: 'bold',
              },
              domainLimit: (min, max) => {
                return { min, max: Math.floor(max + (max * 10) / 100) };
              },
            },
          ]}
          grid={{ horizontal: true }}
          {...getResponsiveConfig()}
        />
      </div>

      {/* Payment Amount Chart */}
      <div>
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-6">
          <div>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1">
              Payment Amount Patterns
            </h3>
            <p className="text-sm text-gray-500">Monthly full vs partial payments</p>
          </div>
        </div>
        <BarChart
          borderRadius={8}
          dataset={amountDataSet}
          xAxis={[
            {
              dataKey: 'month',
              tickLabelStyle: {
                fontSize: 12,
                fill: COLORS.TEXT_SECONDARY,
                fontFamily: 'Inter',
                fontWeight: 'bold',
              },
            },
          ]}
          series={[
            {
              dataKey: 'fullPayments',
              label: 'Full Payments',
              color: COLORS.PRIMARY_500,
              valueFormatter: (value: number | null) => {
                return `${value?.toLocaleString() || 0} payments`;
              },
              stack: 'total',
            },
            {
              dataKey: 'partialPayments',
              label: 'Partial Payments',
              color: COLORS.WARNING_500,
              valueFormatter: (value: number | null) => {
                return `${value?.toLocaleString() || 0} payments`;
              },
              stack: 'total',
            },
          ]}
          yAxis={[
            {
              width: 60,
              tickLabelStyle: {
                fontSize: 11,
                fill: COLORS.TEXT_SECONDARY,
                fontFamily: 'Inter',
                fontWeight: 'bold',
              },
              domainLimit: (min, max) => {
                return { min, max: Math.floor(max + (max * 10) / 100) };
              },
            },
          ]}
          grid={{ horizontal: true }}
          {...getResponsiveConfig()}
        />
      </div>
    </div>
  );
}

export default PaymentPatternsChart;
